import { DEM_URL, DEM_TILE_SIZE } from './dem-config.js'
import { tileCacheService } from '../tile-cache/tile-cache-service.js'

const TILE_SIZE = DEM_TILE_SIZE
const CACHE_MAX = 128
const SOURCE_ID = 'terrarium-dem'

class DemTileCache {
  private cache = new Map<string, Uint8ClampedArray>()
  private inflight = new Map<string, Promise<Uint8ClampedArray | null>>()

  async getTile(
    z: number,
    x: number,
    y: number,
    signal?: AbortSignal
  ): Promise<Uint8ClampedArray | null> {
    const key = `${z}/${x}/${y}`

    const cached = this.cache.get(key)
    if (cached) return cached

    const pending = this.inflight.get(key)
    if (pending) return pending

    const promise = this.fetchAndDecode(key, z, x, y, signal)
    this.inflight.set(key, promise)

    try {
      return await promise
    } finally {
      this.inflight.delete(key)
    }
  }

  private async fetchAndDecode(
    key: string,
    z: number,
    x: number,
    y: number,
    signal?: AbortSignal
  ): Promise<Uint8ClampedArray | null> {
    try {
      const url = DEM_URL.replace('{z}', String(z))
        .replace('{x}', String(x))
        .replace('{y}', String(y))

      const blob = await tileCacheService.getTile(SOURCE_ID, url, z, x, y, signal ? { signal } : {})
      if (!blob) return null

      const bitmap = await createImageBitmap(blob, {
        colorSpaceConversion: 'none',
        premultiplyAlpha: 'none',
      })

      const canvas = new OffscreenCanvas(TILE_SIZE, TILE_SIZE)
      const ctx = canvas.getContext('2d', { willReadFrequently: true })!
      ctx.drawImage(bitmap, 0, 0, TILE_SIZE, TILE_SIZE)
      const imageData = ctx.getImageData(0, 0, TILE_SIZE, TILE_SIZE)
      bitmap.close()

      if (this.cache.size >= CACHE_MAX) {
        const firstKey = this.cache.keys().next().value!
        this.cache.delete(firstKey)
      }
      this.cache.set(key, imageData.data)
      return imageData.data
    } catch {
      return null
    }
  }
}

export const demTileCache = new DemTileCache()
